import React, { useEffect, useRef, useState } from 'react';
import { Button, InputNumber, Switch } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { effectiveCapacity, applyToggle, emptyIntervention, isOneWayEdge } from '../model/interventions';
import { vcColor } from '../model/metrics';

export default function LinkDetailPanel({
  feature,
  meta,
  draft,
  setDraft,
  onClose,
  onApply,
}) {
  const panelRef = useRef(null);
  const dragRef = useRef(null);
  const cleanupDragRef = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => () => cleanupDragRef.current?.(), []);

  if (!feature || !draft) return null;
  const props = feature.properties || {};
  const oneway = isOneWayEdge(meta, props);
  const baseCap = Number(meta?.capacity_vph ?? props.capacity_vph ?? 0) || 1;
  const flow = Number(props.flow ?? 0);
  const cap = effectiveCapacity(baseCap, draft);
  const vc = cap ? flow / cap : 0;
  const hasObservedSpeed = !!(props.is_new || props.speed_is_observed || meta?.speed_is_observed);
  const rawSpeed = hasObservedSpeed ? (meta?.speed_kph ?? props.speed_kph) : null;
  const speed = rawSpeed == null || rawSpeed === '' ? null : Math.round(Number(rawSpeed));
  const resulting = Math.max(1, Math.round(Number(draft.newCap ?? baseCap) * Number(draft.factor ?? 1)));

  const toggle = (key) => setDraft((prev) => applyToggle(prev || emptyIntervention(), key, oneway));

  const startDrag = (event) => {
    if (event.button !== 0 || !panelRef.current) return;
    const rect = panelRef.current.getBoundingClientRect();
    cleanupDragRef.current?.();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
    setPosition({ x: rect.left, y: rect.top });

    const move = (moveEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const margin = 8;
      const maxX = Math.max(margin, window.innerWidth - drag.width - margin);
      const maxY = Math.max(margin, window.innerHeight - drag.height - margin);
      setPosition({
        x: Math.min(Math.max(margin, moveEvent.clientX - drag.offsetX), maxX),
        y: Math.min(Math.max(margin, moveEvent.clientY - drag.offsetY), maxY),
      });
    };
    const stop = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', stop);
      cleanupDragRef.current = null;
    };
    cleanupDragRef.current = stop;
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', stop);
    event.preventDefault();
  };

  return (
    <div className="assignx-modal-layer">
      <div
        ref={panelRef}
        className="assignx-selection-popup-v2 assignx-link-panel"
        style={position ? { left: position.x, top: position.y, transform: 'none' } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <button className="assignx-popup-v2-close" onClick={onClose}><CloseOutlined /></button>

        <div
          className="assignx-popup-v2-header assignx-drag-handle"
          onMouseDown={startDrag}
        >
          <div className="assignx-popup-v2-title">{props.name || 'Strada'}</div>
          <div className="assignx-popup-v2-subtitle">
            {[meta?.highway || props.highway, oneway ? 'SENSO UNICO' : 'DOPPIO SENSO'].filter(Boolean).join(' · ')}
          </div>
        </div>

        <div className="assignx-popup-v2-body">
          <div className="assignx-link-kpis">
            <div><b>{Math.round(flow)}</b><span>FLUSSO V/H</span></div>
            <div><b>{cap}</b><span>CAPACITÀ</span></div>
            <div><b style={{ color: vcColor(vc) }}>{vc.toFixed(2).replace('.', ',')}</b><span>V/C</span></div>
            {Number.isFinite(speed) && speed > 0 && <div><b>{speed}</b><span>LIMITE KM/H</span></div>}
          </div>

          <div className="assignx-link-sect">Configurazione direzioni</div>

          {!oneway && (
            <>
              <button
                type="button"
                className={`assignx-dir-btn ${(draft.forward || draft.both) ? 'active' : ''}`}
                onClick={() => toggle('forward')}
              >
                Direzione principale
                <span className="assignx-dir-state">{(draft.forward || draft.both) ? 'Chiusa' : 'Aperta'}</span>
              </button>
              <button
                type="button"
                className={`assignx-dir-btn ${(draft.backward || draft.both) ? 'active' : ''}`}
                onClick={() => toggle('backward')}
              >
                Direzione opposta
                <span className="assignx-dir-state">{(draft.backward || draft.both) ? 'Chiusa' : 'Aperta'}</span>
              </button>
              <div className="assignx-swrow">
                <span>
                  Chiudi entrambe le direzioni
                  <small>Chiude la strada in entrambi i sensi di marcia</small>
                </span>
                <Switch checked={!!draft.both} onChange={() => toggle('both')} />
              </div>
            </>
          )}

          {oneway && (
            <button
              type="button"
              className={`assignx-dir-btn ${draft.forward ? 'active' : ''}`}
              onClick={() => toggle('forward')}
            >
              Chiudi strada (senso unico)
              <span className="assignx-dir-state">{draft.forward ? 'Chiusa' : 'Aperta'}</span>
            </button>
          )}

          <div className={`assignx-swrow ${oneway ? '' : 'disabled'}`}>
            <span>
              Inverti senso di marcia
              <small>{oneway ? 'Inverte il senso di marcia di questa strada' : 'Disponibile solo su strade a senso unico'}</small>
            </span>
            <Switch checked={!!draft.reverse} disabled={!oneway} onChange={() => oneway && toggle('reverse')} />
          </div>

          <div className="assignx-link-sect">Capacità stradale</div>
          <div className="assignx-cap-grid">
            <label>
              {meta?.capacity_estimated ? 'Capacità stimata' : 'Capacità dai dati'}
              <div className="assignx-cap-readonly">{baseCap} v/h</div>
            </label>
            <label>
              Nuova capacità (v/h)
              <InputNumber min={1} value={draft.newCap ?? baseCap} onChange={(v) => setDraft((p) => ({ ...p, newCap: v }))} />
            </label>
            <label>
              Fattore capacità
              <InputNumber min={0.1} step={0.1} value={draft.factor ?? 1} onChange={(v) => setDraft((p) => ({ ...p, factor: v }))} />
            </label>
            <label>
              Capacità risultante
              <div className="assignx-cap-readonly">{resulting} v/h</div>
            </label>
          </div>
        </div>

        <div className="assignx-popup-v2-footer">
          <Button
            type="primary"
            block
            className="assignx-confirm-assetto-btn"
            style={{ background: '#353A0A' }}
            onClick={onApply}
          >
            APPLICA MODIFICHE ALLO SCENARIO
          </Button>
        </div>
      </div>
    </div>
  );
}
